import { useState } from 'react';
import { BarChart3, ChevronDown } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  formatDelta,
  formatEuros,
  formatRecapDay,
  recapSections,
  recapVisitors,
  type RecapStats,
} from '@/lib/news/daily-recap';
import type { DailyRecapFeedRow } from './types';

/** Chiffres clés affichés replié : visiteurs, démos, inscriptions, connexions, achats. */
export function recapHeadlineFigures(
  s: RecapStats,
): { label: string; value: string; delta: string }[] {
  const n = (v: number | undefined) => v ?? 0;
  const p = s.prev;
  // Visiteurs uniques (migration 0692) ; un ancien récap n'a que les visites.
  const hasVisitors = s.visits?.visitors !== undefined;
  const visits = recapVisitors(s.visits);
  const prevVisits =
    p && (p.visits?.visitors !== undefined) === hasVisitors ? recapVisitors(p.visits) : undefined;
  const cents = n(s.purchases?.skillcards_cents);
  return [
    {
      label: hasVisitors ? 'Visiteurs' : 'Visites',
      value: String(visits),
      delta: formatDelta(visits, prevVisits),
    },
    {
      label: 'Démos',
      value: String(n(s.demos?.started)),
      delta: formatDelta(n(s.demos?.started), p ? n(p.demos?.started) : undefined),
    },
    {
      label: 'Inscriptions',
      value: String(n(s.accounts?.signups)),
      delta: formatDelta(n(s.accounts?.signups), p ? n(p.accounts?.signups) : undefined),
    },
    {
      label: 'Connexions',
      value: String(n(s.accounts?.logins)),
      delta: formatDelta(n(s.accounts?.logins), p ? n(p.accounts?.logins) : undefined),
    },
    {
      label: 'Achats',
      value: formatEuros(cents),
      delta: formatDelta(
        n(s.purchases?.skillcards) + n(s.purchases?.passes) + n(s.purchases?.subscriptions),
        p
          ? n(p.purchases?.skillcards) + n(p.purchases?.passes) + n(p.purchases?.subscriptions)
          : undefined,
      ),
    },
  ];
}

const deltaTone = (d: string) =>
  d.startsWith('+')
    ? 'text-green-700 ocean:text-green-300'
    : d.startsWith('−')
      ? 'text-red-600 ocean:text-red-300'
      : 'text-ink-400';

/**
 * Carte du Feed admin « Récap du mardi 14 octobre » (migration 0523) : chiffres
 * clés et écarts avec la veille, dépliable pour tout le détail (mêmes sections
 * que le mail). Comptes admin et @jeraisonne.fr exclus.
 */
export function DailyRecapCard({ data }: { data: DailyRecapFeedRow }) {
  const [open, setOpen] = useState(false);
  const stats = { ...data.stats, day: data.stats.day ?? data.day };
  const figures = recapHeadlineFigures(stats);
  return (
    <div className="border-ink-100 ocean:bg-white/5 space-y-3 rounded-2xl border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="bg-primary-50 text-primary-600 ocean:bg-white/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
          <BarChart3 className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-primary-600 text-[11px] font-semibold uppercase tracking-wider">
            Récap de la journée
          </p>
          <p className="text-ink-900 text-sm font-semibold first-letter:uppercase">
            {formatRecapDay(stats.day)}
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {figures.map((f) => (
          <div
            key={f.label}
            className="bg-ink-50 ocean:bg-white/5 rounded-xl px-2 py-2 text-center"
          >
            <dt className="text-ink-500 text-xs">{f.label}</dt>
            <dd className="text-ink-900 text-base font-bold tabular-nums">{f.value}</dd>
            {f.delta && (
              <dd className={cn('text-xs tabular-nums', deltaTone(f.delta))}>{f.delta}</dd>
            )}
          </div>
        ))}
      </dl>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-primary-700 flex min-h-[2.75rem] w-full items-center justify-center gap-1 rounded-xl text-sm font-semibold active:scale-[0.99]"
      >
        {open ? 'Masquer le détail' : 'Voir le détail'}
        <ChevronDown
          className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open && (
        <div className="space-y-4">
          {recapSections(stats).map((sec) => (
            <section key={sec.title}>
              <h3 className="text-ink-500 text-xs font-semibold uppercase tracking-wider">
                {sec.title}
              </h3>
              <ul className="divide-ink-100 mt-1 divide-y">
                {sec.lines.map((l) => {
                  const delta = l.num !== undefined ? formatDelta(l.num, l.prevNum) : '';
                  return (
                    <li key={l.label} className="flex items-start gap-3 py-2 text-sm">
                      <span className="min-w-0 flex-1">
                        <span className="text-ink-800">{l.label}</span>
                        {l.detail && (
                          <span className="text-ink-500 block break-words text-xs">{l.detail}</span>
                        )}
                      </span>
                      <span className="text-ink-900 font-semibold tabular-nums">{l.value}</span>
                      <span className={cn('w-8 text-right text-xs tabular-nums', deltaTone(delta))}>
                        {delta}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          <p className="text-ink-400 text-xs">
            Comptes admin et @jeraisonne.fr exclus, robots exclus. Un visiteur = un appareil sur la
            journée (un compte connecté sur plusieurs appareils compte une fois).
          </p>
        </div>
      )}
    </div>
  );
}
